import React from "react";
import {
  PivotGrid,
  PivotGridContainer,
  PivotGridConfigurator,
  PivotGridCell,
  sumAggregate,
  minAggregate,
  maxAggregate,
  averageAggregate,
  usePivotLocalDataService,
  PivotGridConfiguratorButton,
} from "@progress/kendo-react-pivotgrid";
import { Loader } from "@progress/kendo-react-indicators";
// import tempData from "./tempdata";
import { format, parseISO } from "date-fns";
import "../../public/css/pivotGrid.scss";

const defaultFilter = [];
const defaultSort = [];
// const measuresNameOpertation = { sumAggregate: customAggregateMeasure, maxAggregate, minAggregate, averageAggregate };
const measuresNameOpertation = { sumAggregate, maxAggregate, minAggregate, averageAggregate };
let pivotProps, configuratorProps, state;
let measures = [],
  defaultAxes = { rowAxes: [], columnAxes: [], measureAxes: [] },
  dimensions = {};

// Utility functions to extract date parts from a date
const getDatePart = (date, part) => {
  switch (part) {
    case "day":
      return format(date, "d");
    case "month":
      return format(date, "MMMM");
    case "year":
      return format(date, "yyyy");
    case "week":
      return format(date, "wo");
    case "weekday":
      return format(date, "EEEE");
    case "quarter":
      return `Q${Math.floor((date.getMonth() + 3) / 3)}`;
    default:
      return date;
  }
};

// Date aggregation functions
const dateAggregates = {
  day: (date) => getDatePart(date, "day"),
  month: (date) => getDatePart(date, "month"),
  year: (date) => getDatePart(date, "year"),
  week: (date) => getDatePart(date, "week"),
  weekday: (date) => getDatePart(date, "weekday"),
  quarter: (date) => getDatePart(date, "quarter"),
};

const App = ({ data, tableConfiguration, changeConfiguration }) => {
  // data = tempData;

  const [show, setShow] = React.useState(false);
  const hasRYGRules = !!(
    tableConfiguration.rygRules &&
    typeof tableConfiguration.rygRules.red === "string" &&
    tableConfiguration.rygRules.red.trim() !== "" &&
    typeof tableConfiguration.rygRules.yellow === "string" &&
    tableConfiguration.rygRules.yellow.trim() !== "" &&
    typeof tableConfiguration.rygRules.green === "string" &&
    tableConfiguration.rygRules.green.trim() !== ""
  );

  const capitaliseFirstLetter = (word) => {
    return word.slice(0, 1).toUpperCase() + word.slice(1);
  };

  // -formating data to support kendo
  //*setting measures
  measures = React.useMemo(() => {
    return tableConfiguration.measures?.map((measure) => ({
      name: measure.name,
      value: (item) => item[measure.associatedField.key],
      aggregate: measure.type === "customAggregate" ? eval(measure.customMeasure) : measuresNameOpertation[measure.type],
    }));
  }, [tableConfiguration.measures]);

  //*setting dimensions
  const dimentionsToFormat = tableConfiguration.fields;
  dimentionsToFormat?.forEach((each) => {
    //all fields that need to be pivoted over need to be in dimensions for it to work
    dimensions[each.name] = {
      //shown in configurator
      caption: each.name, //shown in table
      displayValue: (item) => item[each.key],
      sortValue: (displayValue) => displayValue,
    };
    if (each.type === "date") {
      each.dateAttributes.forEach((dateAttribute) => {
        //key needs to be unique
        dimensions[`${each.name} (${capitaliseFirstLetter(dateAttribute)})`] = {
          caption: `${each.name} (${capitaliseFirstLetter(dateAttribute)})`, //shown in coumn name and configurator
          displayValue: (item) => dateAggregates[dateAttribute](parseISO(item[each.key])),
          sortValue: (displayValue) => displayValue,
          type: "date",
        };
      });
    }
  });

  //*setting default axes
  tableConfiguration?.pivotProps &&
    Object.keys(tableConfiguration.pivotProps).forEach((key, index) => {
      if (key)
        defaultAxes[key] =
          tableConfiguration.pivotProps[key] && Object.keys(tableConfiguration.pivotProps[key])?.length > 0
            ? // ? [{ name: [tableConfiguration.pivotProps[key][0]?.select?.name], expand: false }]
              tableConfiguration.pivotProps[key]
            : [];
    });

  const serviceArgs = {
    dimensions, //all dimension to pivot over
    measures, //opertations performed on each cell
    data, //data on which pivot grid works
    defaultRowAxes: defaultAxes.rowAxes,
    defaultColumnAxes: defaultAxes.columnAxes,
    defaultMeasureAxes: defaultAxes.measureAxes,
    defaultSort,
    defaultFilter,
  };

  try {
    //!throws an error is configuration is not right, hence the try catch
    ({ pivotProps, configuratorProps, state } = usePivotLocalDataService(serviceArgs));
  } catch (err) {
    console.log(err);
    //!preventing re renders
    if (show !== false) {
      setShow(false);
    }
  }
  //*Manipulating configuration to add children
  //removing all the date fields present in dimesions
  //doing this after the local hook call so kendo adds all the dimentions to pivotprops
  const keysToRemove = tableConfiguration?.fields
    ?.filter((field) => field.type === "date")
    .reduce((acc, val) => {
      acc.push(val.name);
      val.dateAttributes.map((attribute) => acc.push(`${val.name} (${capitaliseFirstLetter(attribute)})`));
      return acc;
    }, []);
  if (configuratorProps?.data) configuratorProps.data = configuratorProps?.data?.filter((item) => !keysToRemove.includes(item.caption));

  //overwriting the configuratorProps.data to dynamically add children and get dropdowns
  const dateFields = tableConfiguration?.fields?.filter((field) => field.type === "date");
  const newData = dateFields?.map((data) => ({
    caption: data.name,
    name: data.name,
    uniqueName: data.name,
    hierarchyUniqueName: data.key,
    measure: true,
    type: 1,
    children: data.dateAttributes?.map((item) => ({
      caption: item, //displayes in configurator
      defaultHierarchy: `${data.name} (${capitaliseFirstLetter(item)})`, //should be present as a dimensions
      description: "",
      name: item,
      uniqueName: item,
      hierarchyUniqueName: item,
      measure: true,
      type: 1,
    })),
  }));
  if (configuratorProps?.data)
    configuratorProps.data = [...(newData?.length > 0 ? newData : []), ...(configuratorProps?.data ? configuratorProps.data : [])];

  //*saving defaults - coming from pivot grid preview
  if (changeConfiguration && typeof changeConfiguration === "function") {
    tableConfiguration.pivotProps = {
      columnAxes: configuratorProps?.columnAxes,
      rowAxes: configuratorProps?.rowAxes,
      measureAxes: configuratorProps?.measureAxes,
    };
    changeConfiguration?.({ target: { value: JSON.stringify(tableConfiguration) } }); //this is how the function accepts the data
  }
  //*saving defaults

  const handleButtonClick = React.useCallback(() => {
    setShow(!show);
  }, [show]);

  const CustomCell = React.forwardRef((props, ref) => {
    const processCondition = (condition) => condition.replace(/(\d+)%/g, (match, number) => `parseFloat(${number})`);
    const value = props.children; //used in eval
    const conditions = {
      red: processCondition(tableConfiguration.rygRules?.red),
      yellow: processCondition(tableConfiguration.rygRules?.yellow),
      green: processCondition(tableConfiguration.rygRules?.green),
    };
    return (
      <PivotGridCell
        ref={ref}
        {...props}
        style={{
          backgroundColor: eval(conditions.red) ? "#ee4623" : eval(conditions.yellow) ? "#f4ba18" : eval(conditions.green) ? "green" : undefined,
        }}
      />
    );
  });

  return (
    <PivotGridContainer>
      <PivotGrid
        cells={{ data: hasRYGRules ? CustomCell : undefined }}
        {...pivotProps}
        style={{
          height: state?.loading ? 350 : undefined,
          maxHeight: state?.loading ? undefined : 700,
        }}
      />
      {show && <PivotGridConfigurator {...configuratorProps} />}
      <PivotGridConfiguratorButton onClick={handleButtonClick} />
      {state?.loading && (
        <Loader
          style={{
            position: "absolute",
            top: "60%",
            left: "50%",
            transform: "translate(-50%,-50%)",
          }}
          size="large"
        />
      )}
    </PivotGridContainer>
  );
};
export default App;
